import { toJS } from "mobx";
import { cast, flow, getEnv, Instance, types } from "mobx-state-tree";
import serializeToQueryParams from "../../helpers/QueryParams";
import { endpoints } from "../api/ApiEndpoints";

import { ApplicationError } from "../error-handler/ErrorHandlerService";
import Language from "../translation/language";
import { translationService } from "../translation/TranslationService";
import {
  PredictionDetails,
  TimestampPrediction,
  emptyVideoAnalysisDataResponse,
  GroupCodesWithMainCodes,
  Segmentation,
  VideoAnalysisDataResponse,
  SecondaryCode,
} from "../../api-models/response/VideoAnalysisDataResponse";
import { featureFlagsStorage } from "../feature-flags/FeatureFlagsStorage";
import { videoPlayerService } from "../video-player/VideoPlayerService";
import { sortHexBasedOnLuminance } from "../../helpers/ColorHelpers";
import { SecondaryCodeEnum } from "../../generated/Damages";
import { ConfidenceOptions } from "../../pages/analysis/components/video-analysis-diagram/tools/Tools";
import { Flags } from "../../generated/FeatureFlags";
import { emptyVideo, Video } from "./Video";

export enum SegmentationStatus {
  InProgress,
  Successful,
  Failed,
}

export interface CodeDetection {
  code: string;
  colorCode: string;
  isVisible?: boolean;
  isDetected?: boolean;
  groupCode?: string;
  secondaryCodes?: SecondaryCode[];
}

export interface DetectionsAtTime {
  [key: string]: {
    mainCode: string;
    C1: boolean;
    C2: string;
  };
}

export interface PredictionGroup {
  mainCode: string;
  hasC1Code: boolean;
  hasC2Code: boolean;
  predictions: Prediction[];
}

interface Prediction {
  secondaryCode: string;
  timestamp: number;
  accuracy: number;
}

export interface ConfidenceLevels {
  [key: string]: number;
}

export const VideoAnalysisServiceModel = types
  .model({
    videoId: types.frozen<number | undefined>(),
    video: types.optional(types.frozen<Video | undefined>(), undefined),
    videoUrl: types.optional(types.string, ""),
    language: types.enumeration<Language>(Object.values(Language)),
    isAnalyzed: types.optional(types.boolean, false),
    isSegmented: false,
    analysisContent: types.optional(types.maybeNull(types.string), null),
    analysisWasSuccessful: types.optional(types.boolean, false),
    errorMessage: types.optional(types.maybeNull(types.string), null),
    analysisInProgress: types.optional(types.boolean, false),
    segmentationInProgress: false,
    segmentationWasSuccessful: types.optional(types.boolean, false),
    isLoading: types.optional(types.boolean, false),
    detectedGroupCodes: types.optional(types.array(types.frozen<CodeDetection>()), []),
    detectedMainCodes: types.optional(types.array(types.frozen<CodeDetection>()), []),
    detectedCharacterizations: types.optional(types.array(types.frozen<CodeDetection>()), []),
    isOverviewDisplayed: true,
    codesAvailableForDetection: types.optional(
      types.array(types.frozen<GroupCodesWithMainCodes>()),
      []
    ),
    vaData: types.optional(
      types.frozen<VideoAnalysisDataResponse>(),
      emptyVideoAnalysisDataResponse
    ),
    plotNeedsRelayout: false,
    showAllSegmentationBars: types.optional(types.boolean, false),

    analysisConfidenceOption: ConfidenceOptions.Recommended,
    firstTimeChoosingAnalysisIndividualConfidence: true,
    shouldShowAnalysisIndivididualConfidenceModal: false,
    analysisIndividualConfidenceLevels: types.optional(types.frozen<ConfidenceLevels>(), {}),
    analysisOverallConfidence: types.optional(types.number, 0.75),

    highlightDetectionConfidenceOption: ConfidenceOptions.Recommended,
    firstTimeChoosingDetectionIndividualConfidence: true,
    shouldShowDetectionIndivididualConfidenceModal: false,
    detectionIndividualConfidenceLevels: types.optional(types.frozen<ConfidenceLevels>(), {}),
    highlightDetectionOverallConfidence: types.optional(types.number, 0.75),
  })
  .views((self) => ({
    get predictionsForSmallestAccuracy() {
      const flatted = self.codesAvailableForDetection
        .map((code) => code.mainCodesWithSecondaryCodes)
        .flat();

      let combinedPredictions: TimestampPrediction[] = [];

      for (const mainCode of flatted) {
        combinedPredictions = [
          ...combinedPredictions,
          ...(Object.values(
            self.vaData.predictionsPerAccuracy[`${mainCode.mainCode}_${"0.65"}`]
          ) as TimestampPrediction[]),
        ];
      }

      return combinedPredictions;
    },

    get allPredictions() {
      const flatted = self.codesAvailableForDetection
        .map((code) => code.mainCodesWithSecondaryCodes)
        .flat();

      let combinedPredictions: TimestampPrediction[] = [];

      for (const mainCode of flatted) {
        var confidence = 0;
        if (self.analysisConfidenceOption === ConfidenceOptions.Recommended) {
          confidence = self.vaData.recommendedConfidenceLevels[mainCode.mainCode]
        } else {
          if (self.analysisConfidenceOption === ConfidenceOptions.Individual) {
            if (self.analysisIndividualConfidenceLevels?.[mainCode.mainCode]) {
              confidence = self.analysisIndividualConfidenceLevels?.[mainCode.mainCode];
            }
          } else {
            confidence = self.analysisOverallConfidence
          }
        }
        
        if (confidence > 1) confidence = confidence / 100;
        var object = self.vaData.predictionsPerAccuracy[mainCode.mainCode + "_" + confidence];
        combinedPredictions = [...combinedPredictions, ...Object.values(object) as TimestampPrediction[]]
      }

      return combinedPredictions;
    },
    get mainCodeColors() {
      return self.codesAvailableForDetection
        .map((code) => code.mainCodesWithSecondaryCodes)
        .flat()
        .reduce((accumulator, value) => {
          return { ...accumulator, [value.mainCode]: value.color };
        }, {});
    },
  }))
  .actions((self) => {
    const getSortedCodesByColor = (codes: GroupCodesWithMainCodes[]): GroupCodesWithMainCodes[] => {
      const sortedCodes: GroupCodesWithMainCodes[] = [];

      codes.forEach((code) => {
        sortedCodes.push({
          groupCode: code.groupCode,
          color: code.color,
          mainCodesWithSecondaryCodes: code.mainCodesWithSecondaryCodes.sort((code1, code2) =>
            sortHexBasedOnLuminance(code1.color, code2.color)
          ),
        });
      });

      return sortedCodes;
    };

    const isMainCodeDetected = (mainCode: string): boolean => {
      return self.predictionsForSmallestAccuracy.some(
        (x) => x.predictionDetails?.mainCode === mainCode
      );
    };

    const isGroupCodeDetected = (groupCode: string): boolean => {
      return self.vaData.segmentationDetails.segmentations.some(
        (x: Segmentation) => x.groupCode === groupCode && x.coordinates.length > 0
      );
    };

    const loadVideoAnalysis = flow(function* () {
      const languageQueryParam = translationService.language === Language.english ? 0 : 1;
      try {
        self.isLoading = true;

        if (featureFlagsStorage.isFeatureEnabled(Flags.Render_VA_Diagram_In_Platform)) {
          const endpointVAData = endpoints.get("video_analysis_data");
          if (endpointVAData) {
            endpointVAData.routeParams = [self.videoId!];
          }

          var responseVAData:any = {};
          try {
            responseVAData = yield getEnv(self).api.get(endpointVAData);
          } catch (error) {
            self.video = new Video({...emptyVideo, isConverted: true, analysisSuccessfullyDone: false, segmentationSuccessfullyDone: false});
          }

          self.video = new Video(responseVAData.video);
          self.isAnalyzed = responseVAData.isAnalyzed;
          self.isSegmented = responseVAData.isSegmented;
          self.analysisWasSuccessful = responseVAData.videoAutoDetectionWasSuccessful;
          self.errorMessage = responseVAData.videoAutoDetectionErrorMessage;
          self.analysisInProgress = responseVAData.videoAutoDetectionIsInProgress;
          self.segmentationInProgress = responseVAData.segmentationIsInProgress;
          self.segmentationWasSuccessful = responseVAData.segmentationWasSuccessful;
          self.codesAvailableForDetection = cast(
            getSortedCodesByColor(responseVAData.codesAvailableForDetection)
          );
          self.vaData = responseVAData;

          self.highlightDetectionOverallConfidence =
            self.vaData.recommendedConfidenceLevels["Highlight_Detection"];
          self.analysisOverallConfidence =
            self.vaData.recommendedConfidenceLevels["Video_Analysis"];

          const mainCodes: CodeDetection[] = [];
          self.codesAvailableForDetection.forEach((groupCode) => {
            groupCode.mainCodesWithSecondaryCodes.forEach((mainCode) => {
              mainCodes.push({
                code: mainCode.mainCode,
                colorCode: mainCode.color,
                isVisible: true,
                secondaryCodes: mainCode.secondaryCodes,
                isDetected: isMainCodeDetected(mainCode.mainCode),
                groupCode: groupCode.groupCode,
              });
            });
          });

          self.detectedMainCodes = cast(mainCodes);

          const characterizationCodes: CodeDetection[] = [];
          for (let key in self.vaData.characterizationCodesWithColors) {
            characterizationCodes.push({
              code: key,
              colorCode: self.vaData.characterizationCodesWithColors[key],
              isVisible: true,
              isDetected: isMainCodeDetected(key),
            });
          }
          self.detectedCharacterizations = cast(characterizationCodes);

          if (featureFlagsStorage.isFeatureEnabled(Flags.Show_Video_Segmentations)) {
            const groupCodes: CodeDetection[] = [];
            self.codesAvailableForDetection.forEach((groupCode) => {
              groupCodes.push({
                code: groupCode.groupCode,
                colorCode: groupCode.color,
                isVisible: true,
                isDetected: isGroupCodeDetected(groupCode.groupCode),
              });
            });
            self.detectedGroupCodes = cast(groupCodes);
            videoPlayerService.setGroupCodesFilterOptions(self.detectedGroupCodes);
          }
        } else {
          const endpoint = endpoints.get("media_file_video_analysis");
          if (endpoint) {
            endpoint.routeParams = [self.videoId!];
            endpoint.queryParams = serializeToQueryParams({ language: languageQueryParam });
          }
          const response = yield getEnv(self).api.get(endpoint);
          self.isAnalyzed = response.isAnalyzed;
          self.analysisContent = response.analysisContent;
          self.analysisWasSuccessful = response.videoAutoDetectionWasSuccessful;
          self.errorMessage = response.videoAutoDetectionErrorMessage;
          self.analysisInProgress = response.videoAutoDetectionIsInProgress;
        }

        self.isLoading = false;
      } catch (error) {
        self.isLoading = false;
        const applicationError = error as ApplicationError;
        getEnv(self).errorHandler.handleErrorInSnackbar(applicationError);
      }
    });

    const startVideoAnalysis = flow(function* () {
      try {
        const endpoint = endpoints.get("media_file_video_analysis");
        if (endpoint) {
          endpoint.routeParams = [self.videoId!];
        }
        self.analysisInProgress = true;
        yield getEnv(self).api.put(endpoint);
      } catch (error) {
        self.analysisInProgress = false;
        const applicationError = error as ApplicationError;
        getEnv(self).errorHandler.handleErrorInSnackbar(applicationError);
        throw applicationError;
      }
    });
    const startSegmentation = flow(function* startSegmentation() {
      try {
        const endpoint = endpoints.get("media_file_segmentation");
        if (endpoint) {
          endpoint.routeParams = [self.videoId!];
        }
        self.segmentationInProgress = true;
        videoPlayerService.setGroupCodesFilterOptions([]);
        yield getEnv(self).api.put(endpoint);
      } catch (error) {
        self.segmentationInProgress = false;
        const applicationError = error as ApplicationError;
        getEnv(self).errorHandler.handleErrorInSnackbar(applicationError);
        throw applicationError;
      }
    });

    return {
      afterCreate() {
        if (self.videoId === undefined) return;
        loadVideoAnalysis();
      },

      async startVideoAnalysis() {
        await startVideoAnalysis();
      },

      async startSegmentation() {
        await startSegmentation();
      },

      async reload() {
        await loadVideoAnalysis();
      },

      setVideoId(id: number) {
        self.videoId = id;
      },

      setLanguage(newLanguage: Language) {
        self.language = newLanguage;
      },

      setAnalysisConfidenceOption(confidenceOption: ConfidenceOptions) {
        self.analysisConfidenceOption = confidenceOption;
      },

      showAnalysisIndivididualConfidenceModal() {
        self.shouldShowAnalysisIndivididualConfidenceModal = true;
      },

      hideAnalysisIndivididualConfidenceModal() {
        self.shouldShowAnalysisIndivididualConfidenceModal = false;
      },

      setHighlightDetectionConfidenceOption(confidenceOption: ConfidenceOptions) {
        self.highlightDetectionConfidenceOption = confidenceOption;
      },

      showDetectionIndivididualConfidenceModal() {
        self.shouldShowDetectionIndivididualConfidenceModal = true;
      },

      hideDetectionIndivididualConfidenceModal() {
        self.shouldShowDetectionIndivididualConfidenceModal = false;
      },

      setAnalysisOverallConfidence(overallConfidence: number) {
        self.analysisOverallConfidence = overallConfidence;
      },

      setIndividualAnalysisConfidence(confidenceLevels: ConfidenceLevels) {
        self.analysisIndividualConfidenceLevels = confidenceLevels;
        self.firstTimeChoosingAnalysisIndividualConfidence = false;
      },

      setHighlightDetectionOverallConfidence(overallConfidence: number) {
        self.highlightDetectionOverallConfidence = overallConfidence;
      },

      setIndividualDetectionConfidence(confidenceLevels: ConfidenceLevels) {
        self.detectionIndividualConfidenceLevels = confidenceLevels;
        self.firstTimeChoosingDetectionIndividualConfidence = false;
      },

      loadMainCodesDetected() {
        const videoAnalysisDiagram: any = window.document.getElementById(
          "damages-video-analysis-iframe"
        );
        if (videoAnalysisDiagram && videoAnalysisDiagram.contentWindow.get_main_codes_dic) {
          const mainCodeDetections = videoAnalysisDiagram.contentWindow.get_main_codes_dic();
          const mainCodes: CodeDetection[] = [];

          for (let key in mainCodeDetections) {
            mainCodes.push({ code: key, colorCode: mainCodeDetections[key], isVisible: true });
          }

          self.detectedMainCodes = cast(mainCodes);
        }
      },

      loadCharacterizationsDetected() {
        const videoAnalysisDiagram: any = window.document.getElementById(
          "damages-video-analysis-iframe"
        );
        if (videoAnalysisDiagram && videoAnalysisDiagram.contentWindow.get_characterizations_dic) {
          const characterizationDetections =
            videoAnalysisDiagram.contentWindow.get_characterizations_dic();
          const characterizations: CodeDetection[] = [];

          for (let key in characterizationDetections) {
            characterizations.push({ code: key, colorCode: characterizationDetections[key] });
          }

          self.detectedCharacterizations = cast(characterizations);
        }
      },

      changeView(isOverview: boolean) {
        self.isOverviewDisplayed = isOverview;
        const videoAnalysisDiagram: any = window.document.getElementById(
          "damages-video-analysis-iframe"
        );
        if (videoAnalysisDiagram && videoAnalysisDiagram.contentWindow.change_preview) {
          videoAnalysisDiagram.contentWindow.change_preview(isOverview);
        }
      },

      toggleMainCode(mainCode: string, isVisible: boolean) {
        const videoAnalysisDiagram: any = window.document.getElementById(
          "damages-video-analysis-iframe"
        );
        if (
          videoAnalysisDiagram &&
          videoAnalysisDiagram.contentWindow.toggle_main_code_visibility
        ) {
          videoAnalysisDiagram.contentWindow.toggle_main_code_visibility(mainCode, isVisible);
        }
      },

      addDamageToGraph(code: string, timestamp: number) {
        const videoAnalysisDiagram: any = window.document.getElementById(
          "damages-video-analysis-iframe"
        );
        if (videoAnalysisDiagram && videoAnalysisDiagram.contentWindow.add_damage_to_graph) {
          videoAnalysisDiagram.contentWindow.add_damage_to_graph(code, timestamp);
        }
      },

      updateDamageSeriesIntoGraph(code: string, timestamps: number[]) {
        const videoAnalysisDiagram: any = window.document.getElementById(
          "damages-video-analysis-iframe"
        );
        if (
          videoAnalysisDiagram &&
          videoAnalysisDiagram.contentWindow.update_damage_series_into_graph
        ) {
          videoAnalysisDiagram.contentWindow.update_damage_series_into_graph(code, timestamps);
        }
      },

      changeMainCodeVisibility(mainCode: string) {
        let mainCodeDetections = toJS(self.detectedMainCodes);
        const index = mainCodeDetections.findIndex((det) => det.code === mainCode);
        mainCodeDetections[index] = {
          ...mainCodeDetections[index],
          isVisible: isMainCodeDetected(mainCode) ? !mainCodeDetections[index].isVisible : false,
        };
        self.detectedMainCodes = cast(mainCodeDetections);

        self.plotNeedsRelayout = true;
      },

      getPredictionsByCode(mainCode: string) {
        var confidence = 0;
        if (self.analysisConfidenceOption === ConfidenceOptions.Recommended) {
          confidence = self.vaData.recommendedConfidenceLevels[mainCode]
        } else {
          if (self.analysisConfidenceOption === ConfidenceOptions.Individual) {
            if (self.analysisIndividualConfidenceLevels?.[mainCode]) {
              confidence = self.analysisIndividualConfidenceLevels?.[mainCode];
            }
          } else {
            confidence = self.analysisOverallConfidence
          }
        }
        if (confidence > 1) confidence = confidence / 100;
        
        var object = self.vaData.predictionsPerAccuracy[mainCode + "_" + confidence];
        const predictions = Object.values(object) as TimestampPrediction[];

        return self.vaData.detectedTimestamps.map((x) => {
          const timestampPrediction = predictions.find((p) => p.timestamp === x);

          if (timestampPrediction) return timestampPrediction.predictionDetails.accuracy;
          return 0;
        });
      },

      hasMainCodeOnly(predictionGroup: PredictionGroup) {
        return !predictionGroup.hasC1Code && !predictionGroup.hasC2Code;
      },

      getSecondaryCodeName(predictionGroup: PredictionGroup) {
        if (this.hasMainCodeOnly(predictionGroup)) {
          return "MC";
        }

        if (predictionGroup.hasC1Code) {
          return SecondaryCodeEnum[SecondaryCodeEnum.C1];
        }

        return SecondaryCodeEnum[SecondaryCodeEnum.C2];
      },

      characterizationAtTime(time: number): DetectionsAtTime {
        const detectedCharacterizations = {};

        const predictionsAtTime = self.allPredictions?.filter(
          (x) => Math.round(x.timestamp) === Math.round(time)
        );

        if (!predictionsAtTime) return {};

        let combinedPredictionsAtTime: PredictionDetails[] = [];

        for (const mainCode of predictionsAtTime) {
          if (mainCode.predictionDetails)
            combinedPredictionsAtTime = [
              ...combinedPredictionsAtTime,
              ...[mainCode.predictionDetails],
            ];
        }

        if (!combinedPredictionsAtTime?.length) return {};

        for (const prediction of combinedPredictionsAtTime) {
          detectedCharacterizations[prediction.mainCode] ??= {};
          if (prediction.c1Code)
            detectedCharacterizations[prediction.mainCode][
              SecondaryCodeEnum[SecondaryCodeEnum.C1]
            ] = prediction.c1Code;
          if (prediction.c2Code)
            detectedCharacterizations[prediction.mainCode][
              SecondaryCodeEnum[SecondaryCodeEnum.C2]
            ] = prediction.c2Code;
        }
        return detectedCharacterizations;
      },

      plotRelayoutDone() {
        self.plotNeedsRelayout = false;
      },

      toggleGroupCodeVisibility(code: string) {
        const updatedOptions = self.detectedGroupCodes.map((option) =>
          option.code === code ? { ...option, isVisible: !option.isVisible } : option
        );

        self.detectedGroupCodes = cast(updatedOptions);
      },

      getGroupCodeColors() {
        return self.codesAvailableForDetection.reduce((accumulator, value) => {
          return { ...accumulator, [value.groupCode.toLowerCase()]: value.color };
        }, {});
      },

      buildPredictionGroupsForDetectedDamages(predictionGroups: PredictionGroup[]) {
        for (const prediction of self.allPredictions) {
          const codePrediction = prediction.predictionDetails;
          const hasMatchingMainCode = (x: PredictionGroup) =>
            x.mainCode === codePrediction.mainCode;

          const hasC1Code = (x: PredictionGroup) => x.hasC1Code && codePrediction.c1Code;

          const hasC2Code = (x: PredictionGroup) => x.hasC2Code && codePrediction.c2Code;

          const hasOnlyMainCode = (x: PredictionGroup) =>
            !x.hasC1Code && !codePrediction.c1Code && !x.hasC2Code && !codePrediction.c2Code;

          const predictionGroupC1 = predictionGroups.find(
            (x) => hasMatchingMainCode(x) && hasC1Code(x)
          );
          const predictionGroupC2 = predictionGroups.find(
            (x) => hasMatchingMainCode(x) && hasC2Code(x)
          );
          const predictionGroupMainCode = predictionGroups.find(
            (x) => hasMatchingMainCode(x) && hasOnlyMainCode(x)
          );

          if (codePrediction.c1Code) {
            predictionGroupC1?.predictions.push({
              accuracy: codePrediction.accuracy,
              secondaryCode: codePrediction.c1Code,
              timestamp: prediction.timestamp,
            });
          }

          if (codePrediction.c2Code) {
            predictionGroupC2?.predictions.push({
              accuracy: codePrediction.accuracy,
              secondaryCode: codePrediction.c2Code,
              timestamp: prediction.timestamp,
            });
          }

          if (!codePrediction.c1Code && !codePrediction.c2Code) {
            predictionGroupMainCode?.predictions.push({
              accuracy: codePrediction.accuracy,
              secondaryCode: "",
              timestamp: prediction.timestamp,
            });
          }
        }
      },
      setShowAllSegmentationBars(showAll: boolean) {
        self.showAllSegmentationBars = showAll;
      },
    };
  })
  .views((self) => ({
    get analysisNotYetStarted(): boolean {
      return !self.analysisInProgress && !self.isAnalyzed;
    },

    get analysisFailed(): boolean {
      return !self.analysisInProgress && self.isAnalyzed && !self.analysisWasSuccessful || !self.video?.analysisSuccessfullyDone;
    },

    get segmentationFailed(): boolean {
      return !self.segmentationInProgress && self.isSegmented && !self.segmentationWasSuccessful;
    },

    get analysisIsAvailable(): boolean {
      return !self.analysisInProgress && self.isAnalyzed && self.analysisWasSuccessful;
    },

    get segmentationIsAvailable(): boolean {
      return !self.segmentationInProgress && self.isSegmented && self.segmentationWasSuccessful;
    },

    get pauses() {
      return self.vaData.detectedTimestamps.map((x) => {
        const timestampIntervalCheck = self.vaData.pauses.find((p) => p.begin <= x && p.end >= x);

        if (timestampIntervalCheck) return 1;
        return 0;
      });
    },

    get segmentationStatus(): SegmentationStatus {
      if (self.segmentationInProgress) {
        return SegmentationStatus.InProgress;
      } else if (this.segmentationFailed) {
        return SegmentationStatus.Failed;
      } else {
        return SegmentationStatus.Successful;
      }
    },

    get allTimestamps() {
      return self.vaData.detectedTimestamps;
    },
  }));

export type VideoAnalysisService = Instance<typeof VideoAnalysisServiceModel>;
